// Improved SettingsPage: better UX, controls, logo upload, favicon, switches, and layout
// Improved Notifications & Email Templates tabs: SMTP config, test button, and flexible template management

import React, { useEffect, useState, useRef, useContext } from 'react';
import BookingModal from '../../components/BookingModal';
import SuperAdminLayout from '../../layouts/SuperAdminLayout';
import {
  Box, Typography, TextField, Button, Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions,
  Switch, FormControlLabel, Paper, Avatar, Divider, InputAdornment, IconButton, useTheme
} from '@mui/material';
import UploadIcon from '@mui/icons-material/Upload';
import { Email, CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import api from '../../services/api';
import NotificationSnackbar from '../../components/NotificationSnackbar';
import { SettingsContext } from '../../context/SettingsContext';

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index} style={{ width: '100%' }}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function EmailTemplateTabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index} style={{ width: '100%' }}>
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

function EmailTemplateRow({ label, value, onChange, theme, logoPreview }) {
  const [previewMode, setPreviewMode] = useState(false);
  const [testDialog, setTestDialog] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testMessage, setTestMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  return (
    <Box
      sx={{
        mb: 3,
        p: 2,
        border: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.divider : '#eee'}`,
        borderRadius: 2,
        bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : '#fafbfc',
        boxShadow: theme.palette.mode === 'dark' ? 2 : 0,
        transition: 'background 0.2s'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2">{label}</Typography>
        <Box>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setPreviewMode(pm => !pm)}
            sx={{ ml: 2 }}
          >
            {previewMode ? 'Edit' : 'Preview'}
          </Button>
          <Button
            variant="contained"
            size="small"
            sx={{ ml: 2 }}
            onClick={() => setTestDialog(true)}
          >
            Send Test Email
          </Button>
        </Box>
      </Box>
      {previewMode ? (
        <Box sx={{
          p: 2,
          bgcolor: theme.palette.background.default,
          borderRadius: 2,
          minHeight: 120,
          border: `1px dashed ${theme.palette.divider}`,
        }}>
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar src={logoPreview} sx={{ width: 40, height: 40 }} variant="rounded" />
            <Typography variant="h6">{value.subject || <i>No subject</i>}</Typography>
          </Box>
          <Box
            sx={{
              fontFamily: 'inherit',
              fontSize: 16,
              color: theme.palette.text.primary,
              minHeight: 80,
              bgcolor: theme.palette.background.paper,
              p: 2,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              overflow: 'auto'
            }}
            dangerouslySetInnerHTML={{ __html: value.body || '<i>No body content</i>' }}
          />
        </Box>
      ) : (
        <>
          <TextField
            label="Subject"
            fullWidth
            sx={{ mb: 1 }}
            value={value.subject}
            onChange={e => onChange({ ...value, subject: e.target.value })}
            InputProps={{
              sx: { bgcolor: theme.palette.background.default }
            }}
          />
          <TextField
            label="Body (HTML supported)"
            multiline
            rows={6}
            fullWidth
            sx={{ mb: 1 }}
            value={value.body}
            onChange={e => onChange({ ...value, body: e.target.value })}
            InputProps={{
              sx: { bgcolor: theme.palette.background.default, fontFamily: 'monospace' }
            }}
            placeholder="You can use HTML tags here"
          />
          <Typography variant="caption" color="text.secondary">
            This template is used for <b>{label}</b> emails. HTML tags are supported.
          </Typography>
          {label === 'Booking Confirmation' && (
            <Box sx={{ mt: 1, mb: 1, p: 1, bgcolor: theme.palette.background.paper, borderRadius: 1, border: `1px dashed ${theme.palette.divider}` }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Available Variables:</Typography>
              <Typography variant="body2" color="text.secondary">
                <b>{'{name}'}</b> - Booker Name<br/>
                <b>{'{packageType}'}</b> - Package Type<br/>
                <b>{'{packageName}'}</b> - Package Name<br/>
                <b>{'{packagePrice}'}</b> - Package Price<br/>
                <b>{'{numberOfPeople}'}</b> - Number of People<br/>
                <b>{'{paymentMsg}'}</b> - Payment Message (if applicable)<br/>
                <span style={{ color: '#888', fontSize: 13 }}>(Use double curly braces: <b>{'{{name}}'}</b> etc. in your template)</span>
              </Typography>
            </Box>
          )}
        </>
      )}
      <Dialog open={testDialog} onClose={() => setTestDialog(false)}>
        <DialogTitle>Send Test Email ({label})</DialogTitle>
        <DialogContent>
          <TextField
            label="Recipient Email"
            type="email"
            fullWidth
            sx={{ mb: 2 }}
            value={testEmail}
            onChange={e => setTestEmail(e.target.value)}
            required
          />
          <TextField
            label="Message"
            fullWidth
            multiline
            minRows={3}
            sx={{ mb: 2 }}
            value={testMessage}
            onChange={e => setTestMessage(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => {
              setSending(true);
              try {
                // Use actual template subject/body
                const payload = {
                  ...value,
                  to: testEmail,
                  message: testMessage
                };
                // You may need to adjust API endpoint to match your backend
                const res = await api.post('/api/settings/test-smtp', payload);
                setResult(res.data.message || 'Test email sent!');
              } catch (err) {
                setResult(err.response?.data?.error || 'Failed to send test email');
              } finally {
                setSending(false);
              }
            }}
            disabled={!testEmail || sending}
          >
            {sending ? 'Sending...' : 'Send Test Email'}
          </Button>
        </DialogActions>
        {result && (
          <Box sx={{ p: 2 }}>
            <Typography color={result.includes('Failed') ? 'error' : 'success'}>{result}</Typography>
          </Box>
        )}
      </Dialog>
    </Box>
  );
}

export default function SettingsPage() {
  const theme = useTheme();
  const [tab, setTab] = useState(0);
  const [emailTemplateTab, setEmailTemplateTab] = useState(0);
  const [themeDialog, setThemeDialog] = useState(false);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [logoUrlInput, setLogoUrlInput] = useState('');
  const [logoPreview, setLogoPreview] = useState('');
  const [faviconPreview, setFaviconPreview] = useState('');
  const [smtp, setSmtp] = useState({
    host: '', port: '', user: '', pass: '', secure: false, from: ''
  });
  const [smtpTest, setSmtpTest] = useState({ loading: false, result: null });
  // For test email dialog in Email Templates tab
  const [testDialog, setTestDialog] = useState({ open: false, template: 0 });
  const [testEmail, setTestEmail] = useState('');
  const [testMessage, setTestMessage] = useState('');
  const [templates, setTemplates] = useState({
    welcome: { subject: '', body: '' },
    booking_confirmation: { subject: '', body: '' },
    payment_receipt: { subject: '', body: '' },
    password_reset: { subject: '', body: '' }
  });
  const [templateActions, setTemplateActions] = useState({
    welcome: 'welcome',
    booking_confirmation: 'booking_confirmation',
    payment_receipt: 'payment_receipt',
    password_reset: 'password_reset'
  });
  const logoInputRef = useRef();
  const faviconInputRef = useRef();
  const settingsContext = useContext(SettingsContext);

  useEffect(() => {
    setLoading(true);
    api.get('/api/settings')
      .then(res => {
        const data = res.data.data || res.data;
        setSettings(data);
        setLogoPreview(data.logoUrl || '');
        setLogoUrlInput(data.logoUrl || '');
        setFaviconPreview(data.faviconUrl || '');
        setTemplates({
          welcome: { subject: data.welcomeEmailSubject, body: data.welcomeEmailBody },
          booking_confirmation: { subject: data.bookingConfirmationSubject, body: data.bookingConfirmationBody },
          payment_receipt: { subject: data.paymentReceiptSubject, body: data.paymentReceiptBody },
          password_reset: { subject: data.passwordResetSubject, body: data.passwordResetBody }
        });
        setSmtp({
          host: data.smtpHost,
          port: data.smtpPort,
          user: data.smtpUser,
          pass: data.smtpPass,
          secure: data.smtpSecure,
          from: data.smtpFrom
        });
      })
      .catch(err => setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to fetch settings', severity: 'error' }))
      .finally(() => setLoading(false));
  }, []);

  const handleTabChange = (e, v) => setTab(v);

  // Logo upload handler
  const handleLogoChange = async e => {
    const file = e.target.files[0];
    if (file) {
      setLogoPreview(URL.createObjectURL(file));
      // Upload to backend
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await api.post('/api/storage/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        if (res.data?.url) {
          setLogoUrlInput(res.data.url);
          setSettings(s => ({ ...s, logoUrl: res.data.url }));
        }
      } catch (err) {
        setSnackbar({ open: true, message: 'Failed to upload logo', severity: 'error' });
      }
    }
  };

  // Save General settings handler
  const handleSaveGeneral = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...settings, logoUrl: logoUrlInput };
      const res = await api.put('/api/settings', payload);
      setSettings(res.data.data);
      setSnackbar({ open: true, message: 'Settings saved successfully!', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to save settings', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };
    // In the General tab, add a text field for logo URL and show preview
    // (Find the section where logo upload is handled and add this logic)
    // Example:
    // <TextField label="Logo URL" value={logoUrlInput} onChange={e => { setLogoUrlInput(e.target.value); setSettings(s => ({ ...s, logoUrl: e.target.value })); setLogoPreview(e.target.value); }} fullWidth sx={{ mb: 2 }} />
  // Favicon upload handler
  const handleFaviconChange = e => {
    const file = e.target.files[0];
    if (file) {
      setFaviconPreview(URL.createObjectURL(file));
      // TODO: Upload logic
    }
  };
  // SMTP test handler (real backend call, with optional email and template)
  const handleTestSmtp = async (sendEmail = false, templateIdx = 0) => {
    setSmtpTest({ loading: true, result: null });
    try {
      let subject = '', body = '';
      if (templateIdx === 0) { subject = templates.welcome.subject; body = templates.welcome.body; }
      else if (templateIdx === 1) { subject = templates.booking_confirmation.subject; body = templates.booking_confirmation.body; }
      else if (templateIdx === 2) { subject = templates.payment_receipt.subject; body = templates.payment_receipt.body; }
      else if (templateIdx === 3) { subject = templates.password_reset.subject; body = templates.password_reset.body; }
      const payload = sendEmail
        ? { ...smtp, to: testEmail, message: testMessage, subject, body }
        : smtp;
      const res = await api.post('/api/settings/test-smtp', payload);
      setSmtpTest({ loading: false, result: { success: res.data.success, message: res.data.message || (res.data.success ? 'SMTP connection successful!' : 'SMTP test failed') } });
      if (sendEmail) setTestDialog({ open: false, template: 0 });
    } catch (err) {
      setSmtpTest({ loading: false, result: { success: false, message: err.response?.data?.error || 'SMTP test failed' } });
    }
  };

  return (
    <SuperAdminLayout>
      <Box sx={{
        maxWidth: 800,
        mx: 'auto',
        mt: 8,
        px: { xs: 1, md: 0 },
        color: theme.palette.text.primary,
        transition: 'background 0.2s'
      }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Settings
        </Typography>
        <NotificationSnackbar
          open={snackbar.open}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          message={snackbar.message}
          severity={snackbar.severity}
        />
        {loading ? <Typography>Loading...</Typography> : (
          <>
            <Paper
              elevation={theme.palette.mode === 'dark' ? 4 : 2}
              sx={{
                mb: 2,
                bgcolor: theme.palette.background.paper,
                borderRadius: 3,
                boxShadow: theme.palette.mode === 'dark' ? 6 : 2,
                transition: 'background 0.2s'
              }}
            >
              <Tabs
                value={tab}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="primary"
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  mb: 3,
                  '.MuiTab-root': {
                    color: theme.palette.text.primary,
                  }
                }}
              >
                <Tab label="General" />
                <Tab label="Notifications" />
                <Tab label="Email Templates" />
                <Tab label="Payments & API" />
                <Tab label="Data & Backup" />
                <Tab label="About & Support" />
              </Tabs>
            </Paper>
            {/* General */}
            <TabPanel value={tab} index={0}>
              <Box component="form" sx={{ mt: 2 }}>
                <TextField
                  label="Platform Name"
                  fullWidth
                  sx={{ mb: 2 }}
                  value={settings.platformName || ''}
                  onChange={e => setSettings(s => ({ ...s, platformName: e.target.value }))}
                  InputProps={{
                    sx: { bgcolor: theme.palette.background.default }
                  }}
                />
                <TextField
                  label="Support Email"
                  fullWidth
                  sx={{ mb: 2 }}
                  value={settings.supportEmail || ''}
                  onChange={e => setSettings(s => ({ ...s, supportEmail: e.target.value }))}
                  InputProps={{
                    sx: { bgcolor: theme.palette.background.default }
                  }}
                />
                <TextField
                  label="Default Currency"
                  select
                  SelectProps={{ native: true }}
                  fullWidth
                  sx={{ mb: 2 }}
                  value={settings.currency || 'USD'}
                  onChange={e => setSettings(s => ({ ...s, currency: e.target.value }))}
                  InputProps={{
                    sx: { bgcolor: theme.palette.background.default }
                  }}
                >
                  <option value="USD">USD</option>
                  <option value="TZS">TZS</option>
                  <option value="KES">KES</option>
                  <option value="EUR">EUR</option>
                </TextField>
                <Divider sx={{ my: 3 }}>Branding</Divider>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, mb: 2 }}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Logo</Typography>
                    <TextField
                      label="Logo URL"
                      value={logoUrlInput}
                      onChange={e => { setLogoUrlInput(e.target.value); setSettings(s => ({ ...s, logoUrl: e.target.value })); setLogoPreview(e.target.value); }}
                      fullWidth
                      sx={{ mb: 1 }}
                    />
                    <Avatar
                      src={logoPreview}
                      sx={{
                        width: 64, height: 64,
                        border: `2px solid ${theme.palette.divider}`,
                        bgcolor: theme.palette.background.paper,
                        boxShadow: theme.palette.mode === 'dark' ? 2 : 0
                      }}
                      variant="rounded"
                    />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Favicon</Typography>
                    <TextField
                      label="Favicon URL"
                      value={faviconPreview}
                      onChange={e => { setFaviconPreview(e.target.value); setSettings(s => ({ ...s, faviconUrl: e.target.value })); }}
                      fullWidth
                      sx={{ mb: 1 }}
                    />
                    <Avatar
                      src={faviconPreview}
                      sx={{
                        width: 40, height: 40,
                        border: `2px solid ${theme.palette.divider}`,
                        bgcolor: theme.palette.background.paper,
                        boxShadow: theme.palette.mode === 'dark' ? 2 : 0
                      }}
                      variant="rounded"
                    />
                  </Box>
                </Box>
                <TextField
                  label="Footer Text"
                  fullWidth
                  sx={{ mb: 2 }}
                  value={settings.footerText || ''}
                  onChange={e => setSettings(s => ({ ...s, footerText: e.target.value }))}
                  InputProps={{
                    sx: { bgcolor: theme.palette.background.default }
                  }}
                />
                <Button variant="contained" color="primary" sx={{ borderRadius: 2, fontWeight: 700 }} onClick={handleSaveGeneral} disabled={loading}>
                  {loading ? 'Saving...' : 'Save General'}
                </Button>
                <Button variant="outlined" sx={{ ml: 2 }} onClick={() => setThemeDialog(true)}>Theme Options</Button>
              </Box>
            </TabPanel>
            {/* Notifications */}
            <TabPanel value={tab} index={1}>
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Notification Preferences</Typography>
                <FormControlLabel
                  control={<Switch checked={!!settings.emailNotifications} onChange={e => setSettings(s => ({ ...s, emailNotifications: e.target.checked }))} />}
                  label="Enable Email Notifications"
                  sx={{ mb: 2 }}
                />
                <FormControlLabel
                  control={<Switch checked={!!settings.inAppNotifications} onChange={e => setSettings(s => ({ ...s, inAppNotifications: e.target.checked }))} />}
                  label="Enable In-App Notifications"
                  sx={{ mb: 2 }}
                />
                <Divider sx={{ my: 3 }}>SMTP Email Configuration</Divider>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                  <TextField
                    label="SMTP Host"
                    value={smtp.host}
                    onChange={e => setSmtp(s => ({ ...s, host: e.target.value }))}
                    sx={{ flex: 1, minWidth: 180 }}
                    InputProps={{
                      sx: { bgcolor: theme.palette.background.default }
                    }}
                  />
                  <TextField
                    label="Port"
                    value={smtp.port}
                    onChange={e => setSmtp(s => ({ ...s, port: e.target.value }))}
                    sx={{ width: 100 }}
                    type="number"
                    InputProps={{
                      sx: { bgcolor: theme.palette.background.default }
                    }}
                  />
                  <TextField
                    label="Username"
                    value={smtp.user}
                    onChange={e => setSmtp(s => ({ ...s, user: e.target.value }))}
                    sx={{ flex: 1, minWidth: 180 }}
                    InputProps={{
                      sx: { bgcolor: theme.palette.background.default }
                    }}
                  />
                  <TextField
                    label="Password"
                    value={smtp.pass}
                    onChange={e => setSmtp(s => ({ ...s, pass: e.target.value }))}
                    sx={{ flex: 1, minWidth: 180 }}
                    type="password"
                    InputProps={{
                      sx: { bgcolor: theme.palette.background.default }
                    }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!smtp.secure}
                        onChange={e => setSmtp(s => ({ ...s, secure: e.target.checked }))}
                      />
                    }
                    label="Secure (SSL/TLS)"
                    sx={{ ml: 2 }}
                  />
                  <TextField
                    label="From Email"
                    value={smtp.from}
                    onChange={e => setSmtp(s => ({ ...s, from: e.target.value }))}
                    sx={{ flex: 1, minWidth: 180 }}
                    InputProps={{
                      sx: { bgcolor: theme.palette.background.default }
                    }}
                  />
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<Email />}
                  onClick={() => setTestDialog(true)}
                  disabled={smtpTest.loading}
                  sx={{ mb: 2 }}
                >
                  Test SMTP / Send Test Email
                </Button>
                <Dialog open={testDialog} onClose={() => setTestDialog(false)}>
                  <DialogTitle>Send Test Email</DialogTitle>
                  <DialogContent>
                    <TextField
                      label="Recipient Email"
                      type="email"
                      fullWidth
                      sx={{ mb: 2 }}
                      value={testEmail}
                      onChange={e => setTestEmail(e.target.value)}
                      required
                    />
                    <TextField
                      label="Message"
                      fullWidth
                      multiline
                      minRows={3}
                      sx={{ mb: 2 }}
                      value={testMessage}
                      onChange={e => setTestMessage(e.target.value)}
                    />
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setTestDialog(false)}>Cancel</Button>
                    <Button
                      variant="contained"
                      onClick={() => handleTestSmtp(true)}
                      disabled={!testEmail || smtpTest.loading}
                    >
                      {smtpTest.loading ? 'Sending...' : 'Send Test Email'}
                    </Button>
                  </DialogActions>
                </Dialog>
                {smtpTest.result && (
                  <Typography sx={{ ml: 2 }} color={smtpTest.result.success ? 'success.main' : 'error.main'}>
                    {smtpTest.result.success ? <CheckCircle fontSize="small" /> : <ErrorIcon fontSize="small" />} {smtpTest.result.message}
                  </Typography>
                )}
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ borderRadius: 2, fontWeight: 700, ml: 2 }}
                  onClick={async () => {
                    setLoading(true);
                    try {
                      await api.put('/api/settings', {
                        ...settings,
                        smtpHost: smtp.host,
                        smtpPort: smtp.port,
                        smtpUser: smtp.user,
                        smtpPass: smtp.pass,
                        smtpSecure: smtp.secure,
                        smtpFrom: smtp.from,
                        emailNotifications: settings.emailNotifications,
                        inAppNotifications: settings.inAppNotifications
                      });
                      setSnackbar({ open: true, message: 'Notification settings saved!', severity: 'success' });
                    } catch (err) {
                      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to save notification settings', severity: 'error' });
                    } finally {
                      setLoading(false);
                    }
                  }}
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Save Notification Settings'}
                </Button>
              </Box>
            </TabPanel>
            {/* Email Templates */}
            <TabPanel value={tab} index={2}>
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Email Templates</Typography>
                <Paper
                  elevation={theme.palette.mode === 'dark' ? 4 : 2}
                  sx={{
                    mb: 2,
                    bgcolor: theme.palette.background.paper,
                    borderRadius: 2,
                    boxShadow: theme.palette.mode === 'dark' ? 6 : 2,
                    transition: 'background 0.2s'
                  }}
                >
                  <Tabs
                    value={emailTemplateTab}
                    onChange={(_, v) => setEmailTemplateTab(v)}
                    indicatorColor="primary"
                    textColor="primary"
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                      mb: 2,
                      '.MuiTab-root': {
                        color: theme.palette.text.primary,
                      }
                    }}
                  >
                    <Tab label="Welcome" />
                    <Tab label="Booking Confirmation" />
                    <Tab label="Payment Receipt" />
                    <Tab label="Password Reset" />
                  </Tabs>
                </Paper>
                <EmailTemplateTabPanel value={emailTemplateTab} index={0}>
                  <EmailTemplateRow
                    label="Welcome"
                    value={templates.welcome}
                    onChange={val => setTemplates(t => ({ ...t, welcome: val }))}
                    theme={theme}
                    logoPreview={logoPreview}
                  />
                </EmailTemplateTabPanel>
                <EmailTemplateTabPanel value={emailTemplateTab} index={1}>
                  <EmailTemplateRow
                    label="Booking Confirmation"
                    value={templates.booking_confirmation}
                    onChange={val => setTemplates(t => ({ ...t, booking_confirmation: val }))}
                    theme={theme}
                    logoPreview={logoPreview}
                  />
                </EmailTemplateTabPanel>
                <EmailTemplateTabPanel value={emailTemplateTab} index={2}>
                  <EmailTemplateRow
                    label="Payment Receipt"
                    value={templates.payment_receipt}
                    onChange={val => setTemplates(t => ({ ...t, payment_receipt: val }))}
                    theme={theme}
                    logoPreview={logoPreview}
                  />
                </EmailTemplateTabPanel>
                <EmailTemplateTabPanel value={emailTemplateTab} index={3}>
                  <EmailTemplateRow
                    label="Password Reset"
                    value={templates.password_reset}
                    onChange={val => setTemplates(t => ({ ...t, password_reset: val }))}
                    theme={theme}
                    logoPreview={logoPreview}
                  />
                </EmailTemplateTabPanel>
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ borderRadius: 2, fontWeight: 700 }}
                    onClick={async () => {
                      setLoading(true);
                      try {
                        await api.put('/api/settings', {
                          ...settings,
                          welcomeEmailSubject: templates.welcome.subject,
                          welcomeEmailBody: templates.welcome.body,
                          bookingConfirmationSubject: templates.booking_confirmation.subject,
                          bookingConfirmationBody: templates.booking_confirmation.body,
                          paymentReceiptSubject: templates.payment_receipt.subject,
                          paymentReceiptBody: templates.payment_receipt.body,
                          passwordResetSubject: templates.password_reset.subject,
                          passwordResetBody: templates.password_reset.body
                        });
                        setSnackbar({ open: true, message: 'Email templates saved!', severity: 'success' });
                      } catch (err) {
                        setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to save templates', severity: 'error' });
                      } finally {
                        setLoading(false);
                      }
                    }}
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : 'Save Templates'}
                  </Button>
                  <Button variant="outlined" sx={{ ml: 2 }} onClick={() => setTestDialog({ open: true, template: emailTemplateTab })}>Send Test Email</Button>
                </Box>
                <Dialog open={testDialog.open} onClose={() => setTestDialog({ open: false, template: 0 })}>
                  <DialogTitle>Send Test Email</DialogTitle>
                  <DialogContent>
                    <TextField
                      label="Recipient Email"
                      type="email"
                      fullWidth
                      sx={{ mb: 2 }}
                      value={testEmail}
                      onChange={e => setTestEmail(e.target.value)}
                      required
                    />
                    <TextField
                      label="Message"
                      fullWidth
                      multiline
                      minRows={3}
                      sx={{ mb: 2 }}
                      value={testMessage}
                      onChange={e => setTestMessage(e.target.value)}
                    />
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setTestDialog({ open: false, template: 0 })}>Cancel</Button>
                    <Button
                      variant="contained"
                      onClick={() => handleTestSmtp(true, testDialog.template)}
                      disabled={!testEmail || smtpTest.loading}
                    >
                      {smtpTest.loading ? 'Sending...' : 'Send Test Email'}
                    </Button>
                  </DialogActions>
                </Dialog>
              </Box>
            </TabPanel>
            {/* Payments & API */}
            <TabPanel value={tab} index={3}>
              <Box sx={{ mt: 2 }}>
                <TextField
                  label="Payment Gateway API Key"
                  fullWidth
                  sx={{ mb: 2 }}
                  value={settings.paymentApiKey || ''}
                  onChange={e => setSettings(s => ({ ...s, paymentApiKey: e.target.value }))}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSettings(s => ({ ...s, paymentApiKey: '' }))}>
                          ✕
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: { bgcolor: theme.palette.background.default }
                  }}
                />
                <TextField
                  label="Payment Gateway Mode"
                  select
                  SelectProps={{ native: true }}
                  fullWidth
                  sx={{ mb: 2 }}
                  value={settings.paymentMode || 'test'}
                  onChange={e => setSettings(s => ({ ...s, paymentMode: e.target.value }))}
                  InputProps={{
                    sx: { bgcolor: theme.palette.background.default }
                  }}
                >
                  <option value="test">Test</option>
                  <option value="live">Live</option>
                </TextField>
                <Divider sx={{ my: 3 }}>Offline Payment Details</Divider>
                <TextField
                  label="Offline Payment Instructions"
                  multiline
                  minRows={3}
                  fullWidth
                  sx={{ mb: 2 }}
                  value={settings.offlinePaymentDetails || ''}
                  onChange={e => setSettings(s => ({ ...s, offlinePaymentDetails: e.target.value }))}
                  InputProps={{
                    sx: { bgcolor: theme.palette.background.default }
                  }}
                  placeholder="e.g. Bank account details, mobile money instructions, etc."
                  helperText="These instructions will be shown to users for offline/manual payments."
                />
                <Divider sx={{ my: 3 }}>API & Webhooks</Divider>
                <TextField
                  label="API Key"
                  fullWidth
                  sx={{ mb: 2 }}
                  value={settings.apiKey || ''}
                  onChange={e => setSettings(s => ({ ...s, apiKey: e.target.value }))}
                  InputProps={{
                    sx: { bgcolor: theme.palette.background.default }
                  }}
                />
                <TextField
                  label="Webhook Endpoint"
                  fullWidth
                  sx={{ mb: 2 }}
                  value={settings.webhookEndpoint || ''}
                  onChange={e => setSettings(s => ({ ...s, webhookEndpoint: e.target.value }))}
                  InputProps={{
                    sx: { bgcolor: theme.palette.background.default }
                  }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ borderRadius: 2, fontWeight: 700 }}
                  onClick={async () => {
                    setLoading(true);
                    try {
                      await api.put('/api/settings', {
                        ...settings,
                        offlinePaymentDetails: settings.offlinePaymentDetails,
                        paymentApiKey: settings.paymentApiKey,
                        paymentMode: settings.paymentMode,
                        apiKey: settings.apiKey,
                        webhookEndpoint: settings.webhookEndpoint
                      });
                      setSnackbar({ open: true, message: 'Payments & API settings saved!', severity: 'success' });
                    } catch (err) {
                      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to save Payments & API settings', severity: 'error' });
                    } finally {
                      setLoading(false);
                    }
                  }}
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Save Payments & API'}
                </Button>
              </Box>
            </TabPanel>
            {/* Data & Backup */}
            <TabPanel value={tab} index={4}>
              <Box sx={{ mt: 2, maxWidth: 600 }}>
                <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                  Data & Backup
                </Typography>
                <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
                  <b>Super Admin Guide:</b> Use <b>Export Data</b> to download a full backup (settings, users, bookings, logs, etc). Use <b>Import Data</b> to restore from a backup file. Only superadmins/admins can perform these actions. <br />
                  <b>Manual Backup</b> is recommended before any major changes or imports.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <Button
                    variant="outlined"
                    disabled={loading}
                    onClick={async () => {
                      setLoading(true);
                      try {
                        const res = await api.get('/api/backup/export', { responseType: 'blob' });
                        const url = window.URL.createObjectURL(new Blob([res.data]));
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', 'backup.zip');
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        setSnackbar({ open: true, message: 'Export successful! Backup downloaded.', severity: 'success' });
                      } catch (err) {
                        setSnackbar({ open: true, message: err.response?.data?.error || 'Export failed', severity: 'error' });
                      } finally {
                        setLoading(false);
                      }
                    }}
                  >
                    {loading ? 'Exporting...' : 'Export Data'}
                  </Button>
                  <input
                    type="file"
                    accept=".json,.zip,application/zip,application/json"
                    style={{ display: 'none' }}
                    id="import-backup-input"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      setLoading(true);
                      const formData = new FormData();
                      formData.append('file', file);
                      try {
                        await api.post('/api/backup/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                        setSnackbar({ open: true, message: 'Import successful! Data restored.', severity: 'success' });
                      } catch (err) {
                        setSnackbar({ open: true, message: err.response?.data?.error || 'Import failed', severity: 'error' });
                      } finally {
                        setLoading(false);
                      }
                    }}
                  />
                  <Button
                    variant="outlined"
                    disabled={loading}
                    onClick={() => document.getElementById('import-backup-input').click()}
                  >
                    {loading ? 'Importing...' : 'Import Data'}
                  </Button>
                  <Button variant="contained" color="secondary" disabled={loading} onClick={async () => {
                    setLoading(true);
                    try {
                      const res = await api.get('/api/backup/export', { responseType: 'blob' });
                      const url = window.URL.createObjectURL(new Blob([res.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', `manual-backup-${Date.now()}.zip`);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      setSnackbar({ open: true, message: 'Manual backup completed and downloaded.', severity: 'success' });
                    } catch (err) {
                      setSnackbar({ open: true, message: err.response?.data?.error || 'Manual backup failed', severity: 'error' });
                    } finally {
                      setLoading(false);
                    }
                  }}>
                    {loading ? 'Processing...' : 'Manual Backup'}
                  </Button>
                </Box>
                {loading && (
                  <Typography sx={{ color: 'primary.main', mt: 2 }}>Processing, please wait...</Typography>
                )}
              </Box>
            </TabPanel>
            {/* About & Support */}
            <TabPanel value={tab} index={5}>
              <Box sx={{ mt: 2, maxWidth: 600 }}>
                <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                  About & Support
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <b>System Version:</b> {settings.version || '1.0.0'}
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <b>Developer:</b> Umoja Digital Solution<br />
                  <b>Email:</b> <a href="mailto:info@umojatech.co.tz">info@umojatech.co.tz</a><br />
                  <b>Alternate Email:</b> <a href="mailto:itselly2025@gmail.com">itselly2025@gmail.com</a><br />
                  <b>Phone:</b> <a href="tel:+255692111174">+255 692111174</a><br />
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <Button variant="outlined" onClick={() => window.open('mailto:info@umojatech.co.tz', '_blank')}>Contact Support</Button>
                  <Button variant="outlined" onClick={() => window.open('/documentation.txt', '_blank')}>Documentation</Button>
                  <Button variant="contained" color="primary" onClick={() => window.open('https://umojatech.co.tz', '_blank')}>Visit Developer Website</Button>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  For technical support, feature requests, or bug reports, contact the developer via the above email or phone.<br />
                  For more information, visit the official website or use the in-app support options.
                </Typography>
                <Divider sx={{ my: 3 }} />
                <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>Test Booking Form</Typography>
                <TestBookingButton />
              </Box>
            </TabPanel>
            {/* --- Test Booking Button for SettingsPage --- */}
            <TestBookingButton />
            {/* Theme dialog */}
            <Dialog open={themeDialog} onClose={() => setThemeDialog(false)}>
              <DialogTitle>Theme Options</DialogTitle>
              <DialogContent>
                <FormControlLabel
                  control={<Switch checked={!!settings.darkMode} onChange={e => setSettings(s => ({ ...s, darkMode: e.target.checked }))} />}
                  label="Dark Mode"
                />
                <FormControlLabel
                  control={<Switch checked={!!settings.autoTheme} onChange={e => setSettings(s => ({ ...s, autoTheme: e.target.checked }))} />}
                  label="Auto Theme"
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setThemeDialog(false)}>Close</Button>
              </DialogActions>
            </Dialog>
          </>
        )}
      </Box>
    </SuperAdminLayout>
  );
}

// TestBookingButton component for SettingsPage (must be outside main component)
function TestBookingButton() {
  const [open, setOpen] = React.useState(false);
  return (
    <>
      <Button variant="contained" color="success" onClick={() => setOpen(true)} sx={{ mb: 2 }}>
        Open Test Booking Form
      </Button>
      <BookingModal open={open} onClose={() => setOpen(false)} />
    </>
  );
}
